/**
 * Admin v2 · Moderation settings.
 *
 * Renders the single admin-tunable value that shapes how a ban lands
 * on a player's score: `banned_correct_penalty_points` — the number of
 * points subtracted from every correct answer a banned user submits.
 *
 * This replaced the old -2200 starting-score handicap; the new value
 * is small, per-correct, and visible in the leaderboard as each answer
 * scores lower than a clean player's. Set to 0 to effectively disable
 * the penalty without unblacklisting anyone.
 *
 * Backend validates range (0-1000) on PATCH; the FE surfaces the
 * server error message as-is on save failure.
 */

import { api } from '../../api.js';
import { errorState } from './common.js';

export async function renderModeration(host) {
  host.innerHTML = `<div style="padding:32px;color:var(--a-faint);">Loading…</div>`;
  let cfg;
  try {
    cfg = await api('/admin/settings/moderation');
  } catch (err) {
    host.innerHTML = errorState(err);
    return;
  }
  _mount(host, cfg);
}

function _mount(host, cfg) {
  let penalty = Number(cfg.banned_correct_penalty_points ?? 100);

  host.innerHTML = `
    <div style="max-width:720px;">
      <div id="mod-status" class="adm2-notice" style="display:none;"></div>

      <div class="adm2-card" style="padding:20px;margin-bottom:16px;">
        <h3 style="margin:0 0 4px;font-size:15px;">Ban penalty</h3>
        <div style="color:var(--a-faint);font-size:12px;line-height:1.55;margin-bottom:16px;">
          Points subtracted from <b>every correct answer</b> a banned
          (blacklisted) player submits during a live trivia. Wrong answers
          are already 0 either way, so the penalty only bites on hits.
          Set to 0 to disable the per-answer deduction without lifting
          anyone's ban.
        </div>

        <div style="display:flex;align-items:center;gap:16px;">
          <label style="display:flex;flex-direction:column;gap:6px;flex:0 0 200px;">
            <span style="color:var(--a-faint);font-size:11px;letter-spacing:0.06em;text-transform:uppercase;">Points per correct</span>
            <input
              type="number"
              id="mod-penalty"
              min="0"
              max="1000"
              step="10"
              value="${penalty}"
              class="mono"
              style="font-size:18px;padding:10px 12px;width:120px;text-align:right;"
            />
          </label>
          <div style="color:var(--a-faint);font-size:12px;line-height:1.55;">
            <div>Range: 0 (disabled) &nbsp;→&nbsp; 1000 (max).</div>
            <div style="margin-top:4px;">Default: <b>100</b>. A 1920-point correct answer becomes ${1920 - penalty}.</div>
          </div>
        </div>
      </div>

      <div style="display:flex;gap:10px;">
        <button id="mod-save" class="adm2-btn adm2-btn-primary">Save changes</button>
        <button id="mod-reset" class="adm2-btn">Discard</button>
      </div>
    </div>
  `;

  const penaltyEl = host.querySelector('#mod-penalty');
  const helperLine = host.querySelector('.adm2-card div:last-child div:last-child');
  penaltyEl.addEventListener('input', () => {
    penalty = Number(penaltyEl.value) || 0;
    if (helperLine) {
      helperLine.innerHTML = `Default: <b>100</b>. A 1920-point correct answer becomes ${1920 - penalty}.`;
    }
  });

  host.querySelector('#mod-reset').addEventListener('click', () => {
    renderModeration(host);
  });

  host.querySelector('#mod-save').addEventListener('click', async () => {
    const btn = host.querySelector('#mod-save');
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const updated = await api('/admin/settings/moderation', {
        method: 'PATCH',
        body: { banned_correct_penalty_points: penalty },
      });
      _showStatus(host, `Saved · penalty = ${updated.banned_correct_penalty_points} pts / correct`, 'ok');
    } catch (err) {
      _showStatus(host, err.message || 'Save failed', 'err');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Save changes';
    }
  });
}

function _showStatus(host, msg, kind) {
  const el = host.querySelector('#mod-status');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  el.style.padding = '10px 14px';
  el.style.borderRadius = '8px';
  el.style.marginBottom = '14px';
  el.style.fontSize = '13px';
  if (kind === 'ok') {
    el.style.background = 'rgba(52,211,153,0.08)';
    el.style.color = '#34D399';
    el.style.border = '1px solid rgba(52,211,153,0.35)';
  } else {
    el.style.background = 'rgba(233,75,162,0.08)';
    el.style.color = '#E94BA2';
    el.style.border = '1px solid rgba(233,75,162,0.35)';
  }
}
